import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { editCampaignName } from 'api/CampaignAPI';
import { WhatsApp } from 'constants/messageMediums';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { useUpdateTemplateName } from 'modules/templateEditor/hooks/useUpdateTemplateName';
import { validateWhatsAppTemplateName } from 'modules/templateIndex/utils';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';

import { useEditableWhatsApp } from 'modules/templateEditor/EditWhatsApp/hooks/useEditableWhatsApp';
import { useCampaignQueryContext } from '../../context/CampaignQueryContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { MAX_LENGTH_TEMPLATE_NAME, MAX_LENGTH_TEMPLATE_NAME_WHATSAPP } from '../constants';
import { displayContent } from '../content';
import { EditableInput } from './EditableInput';

type Props = {
  isCompact?: boolean;
  isInverted?: boolean;
  savedRowName?: string;
  isNameInputDisabled?: boolean;
};

export const ContentNameInput = ({
  savedRowName,
  isCompact = false,
  isInverted = false,
  isNameInputDisabled = false,
}: Props) => {
  const { initialTemplate, liveTemplate, setCurrentTemplateField } = useEditableFieldsContext();
  const {
    campaign,
    isLoading: isLoadingCampaign,
    refetch: refetchCampaign,
  } = useCampaignQueryContext();
  const dispatch = useDispatch();
  const { onUpdateTemplateName } = useUpdateTemplateName();
  const { campaignId } = useQueryParamContext();
  const { medium } = useTemplateQueryContext();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const [campaignName, setCampaignName] = useState(campaign?.name);
  const isCampaign = !!campaignId;
  const isWhatsApp = medium === WhatsApp;
  const isUneditableWhatsApp = isWhatsApp && !isEditableWhatsAppTemplate;

  const handleBlur = () => {
    if (isCampaign) {
      if (campaignName !== campaign?.name) {
        // only commit if a change has been made
        editCampaignName(campaignId, campaignName)
          .then(() => {
            dispatch(
              addPositiveToast(
                displayContent.strDataUpdatedSuccessfully({ dataName: campaignName }),
              ),
            );
            refetchCampaign();
          })
          .catch((error: any) => {
            dispatch(addNegativeToast(displayContent.errUpdateCampaignName, error));
          });
      }
    } else if (initialTemplate?.name !== liveTemplate?.name) {
      const isValidTemplateName = !isWhatsApp || validateWhatsAppTemplateName(liveTemplate?.name);
      if (isValidTemplateName) {
        onUpdateTemplateName(liveTemplate.name);
      } else {
        setCurrentTemplateField('name', initialTemplate?.name);
        dispatch(addNegativeToast(displayContent.errUpdateTemplateNameInvalid));
      }
    }
  };

  const handleChange = (newName: string) => {
    if (isCampaign) {
      setCampaignName(newName);
    } else if (isWhatsApp) {
      // WhatsApp templates don't allow spaces so go ahead and replace them as user inputs the template name
      setCurrentTemplateField('name', newName.replaceAll(' ', '_'));
    } else {
      setCurrentTemplateField('name', newName);
    }
  };

  const handleReset = () => {
    if (isCampaign) {
      setCampaignName(campaign?.name);
    } else {
      setCurrentTemplateField('name', initialTemplate.name);
    }
  };

  useEffect(() => {
    if (!isLoadingCampaign) {
      setCampaignName(campaign.name);
    }
  }, [isLoadingCampaign, campaign]);

  return (
    <EditableInput
      isCompact={isCompact}
      isInverted={isInverted}
      isRequired
      maxLength={isWhatsApp ? MAX_LENGTH_TEMPLATE_NAME_WHATSAPP : MAX_LENGTH_TEMPLATE_NAME}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={isCampaign ? displayContent.labelCampaignName : displayContent.labelTemplateName}
      reset={handleReset}
      isDisabled={!!savedRowName || isUneditableWhatsApp || isNameInputDisabled}
      value={savedRowName || (isCampaign ? campaignName : liveTemplate.name)}
    />
  );
};
